
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { PracticeSession } from '@/types/program';

export const usePracticeSession = (teamId?: string) => {
  const [sessions, setSessions] = useState<PracticeSession[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSessions = useCallback(async () => {
    if (!teamId) {
      setSessions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('practice_sessions')
        .select(`
          *,
          programs(title)
        `)
        .eq('team_id', teamId)
        .order('session_date', { ascending: false });

      if (error) throw error;
      setSessions(data || []);
    } catch (error) {
      console.error('Error fetching practice sessions:', error);
      toast.error('Failed to load practice sessions');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  const createSession = async (sessionData: Omit<PracticeSession, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      
      const { data, error } = await supabase
        .from('practice_sessions')
        .insert([sessionData])
        .select()
        .single();

      if (error) throw error;
      
      toast.success('Practice session scheduled successfully');
      await fetchSessions();
      return data;
    } catch (error) {
      console.error('Error creating practice session:', error);
      toast.error('Failed to schedule practice session');
      throw error;
    }
  };

  const updateSession = async (id: string, updates: Partial<PracticeSession>) => {
    try {
      
      const { data, error } = await supabase
        .from('practice_sessions')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      toast.success('Practice session updated successfully');
      await fetchSessions();
      return data;
    } catch (error) {
      console.error('Error updating practice session:', error);
      toast.error('Failed to update practice session');
      throw error;
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  return {
    sessions,
    loading,
    fetchSessions,
    createSession,
    updateSession
  };
};
